<template>
  <div class="dashboard">
    <div class="dashboard__greeting">
      <h5 class="dashboard__hello">Olá, {{ displayName }}</h5>
      <p class="dashboard__cpf">CPF: {{ maskedCpf }}</p>
    </div>

    <template v-if="loadingDashboardDebts">
      <div class="dashboard__debts-loading" role="status" aria-live="polite" aria-busy="true">
        <ProgressLoading :loading="true" />
      </div>
    </template>

    <template v-else>
      <h6
        v-if="!hasClosedAgreementBoticario && !hasClosedAgreementEudora"
        class="dashboard__section-title"
      >
        Que bom te ver por aqui! Veja as oportunidades disponíveis para você:
      </h6>

      <h6 v-if="showOffersWithAgreementIntro" class="dashboard__section-title dashboard__section-title--offers">
        Que bom te ver por aqui, confira essas ofertas:
      </h6>

      <div class="dashboard__cards">
        <div class="dashboard__cards-group" v-if="hasActiveDebtsBoticario">
          <DebtCard
            v-for="(debt, index) in debtsBoticario"
            :key="'botic-' + (debt.document || '') + '-' + (debt.product || '') + '-' + index"
            :debt="debt"
            brand="boticario"
            @negociar="onNegociarBoticario"
          />
        </div>

        <div class="dashboard__cards-group" v-if="hasActiveDebtsEudora">
          <DebtCard
            :debt="eudoraDebtCard"
            brand="eudora"
            @negociar="onNegociarEudora"
          />
        </div>
      </div>

      <hr
        v-if="showDividerBeforeAgreements"
        class="dashboard__agreements-divider"
        aria-hidden="true"
      />

      <div
        v-if="hasClosedAgreementBoticario || hasClosedAgreementEudora"
        class="dashboard__agreements-section"
        :class="{ 'dashboard__agreements-section--after-debt-cards': showDividerBeforeAgreements }"
      >
        <div class="dashboard__agreements-heading">
          <h6 class="dashboard__agreements-title">Meus acordos</h6>
          <p class="dashboard__agreements-subtitle">
            {{ agreementsSectionSubtitle }}
          </p>
        </div>

        <div class="dashboard__agreements-cards-row">
          <div
            v-if="hasClosedAgreementBoticario"
            :class="['dashboard__agreements-wrap', { 'dashboard__agreements-wrap--after-offers': hasActiveDebtsBoticario }]"
          >
            <AgreementCard
              :agreements="preventiveBoticario"
              brand="boticario"
              :agreement-bill-loading-key="loadingAgreementBillKey"
              @request-second-bill="onRequestSecondBill"
            />
          </div>

          <div
            v-if="hasClosedAgreementEudora"
            :class="['dashboard__agreements-wrap', { 'dashboard__agreements-wrap--after-offers': hasActiveDebtsEudora }]"
          >
            <AgreementCard
              :agreements="preventiveEudora"
              brand="eudora"
              :agreement-bill-loading-key="loadingAgreementBillKey"
              @request-second-bill="onRequestSecondBill"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { formatDocument } from '@/utils/helpers';
import flashMessages from '@/utils/flash-messages';
import ProgressLoading from '@/modules/auth/components/ProgressLoading';
import DebtCard from './DebtCard.vue';
import AgreementCard from './AgreementCard.vue';

export default {
  name: 'HomeDashboard',
  components: { ProgressLoading, DebtCard, AgreementCard },
  data: () => ({}),
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('negotiation', [
      'debtsBoticario', 'debtsEudora', 'offersEudora',
      'preventiveBoticario', 'preventiveEudora', 'loadingDashboardDebts',
      'loadingAgreementBillKey',
    ]),
    ...mapGetters('negotiation', ['hasActiveDebtsBoticario', 'hasActiveDebtsEudora']),
    displayName() {
      let name = this.user?.FullName;
      if (!name) {
        try {
          const stored = localStorage.getItem('user');
          if (stored) name = JSON.parse(stored)?.FullName;
        } catch { name = null; }
      }
      const normalized = typeof name === 'string' ? name.trim() : '';
      const doc = this.user?.DocNumber ? String(this.user.DocNumber).replace(/\D/g, '') : '';
      const nameDigits = normalized.replace(/\D/g, '');
      const isJustDocument = normalized !== '' && (
        normalized === this.user?.DocNumber
        || (doc !== '' && nameDigits === doc)
      );
      if (normalized === '' || isJustDocument) return 'Usuário';
      return normalized.includes(' ') ? normalized.split(' ')[0] : normalized;
    },

    maskedCpf() {
      if (!this.user?.DocNumber) return '';
      const doc = formatDocument(this.user.DocNumber);
      return doc.split('.')[0].substring(0, 6) + '.xxx.xxx-' + doc.split('-')[1];
    },

    hasClosedAgreementBoticario() {
      return Array.isArray(this.preventiveBoticario) && this.preventiveBoticario.length > 0;
    },

    hasClosedAgreementEudora() {
      return Array.isArray(this.preventiveEudora) && this.preventiveEudora.length > 0;
    },

    showOffersWithAgreementIntro() {
      return (this.hasClosedAgreementBoticario && this.hasActiveDebtsBoticario)
        || (this.hasClosedAgreementEudora && this.hasActiveDebtsEudora);
    },

    showDividerBeforeAgreements() {
      const hasAgreements = this.hasClosedAgreementBoticario || this.hasClosedAgreementEudora;
      const hasDebtCards = this.hasActiveDebtsBoticario || this.hasActiveDebtsEudora;
      return hasAgreements && hasDebtCards;
    },

    closedAgreementsCount() {
      const b = Array.isArray(this.preventiveBoticario) ? this.preventiveBoticario.length : 0;
      const e = Array.isArray(this.preventiveEudora) ? this.preventiveEudora.length : 0;
      return b + e;
    },

    agreementsSectionSubtitle() {
      const n = this.closedAgreementsCount;
      if (n <= 1) {
        return 'Você tem um acordo ativo com o Grupo Boticário.';
      }
      return `Você tem ${n} acordos ativos com o Grupo Boticário.`;
    },

    eudoraDebtCard() {
      if (!this.debtsEudora || !this.debtsEudora[0]) return null;
      const debt = this.debtsEudora[0];
      return {
        ...debt,
        offersEudora: this.offersEudora,
        installments: this.offersEudora[0]?.installments || '',
        originalValue: this.offersEudora[0]?.originalValue,
        earliestDueDate: this.offersEudora[0]?.earliestDueDate || debt.earliestDueDate,
        updatedValueFormatted: this.offersEudora[0]?.updatedValueFormatted,
        updatedValue: this.offersEudora[0]?.updatedValue,
      };
    },
  },
  methods: {
    ...mapActions('negotiation', [
      'ActionNegotiationSelectBoticario',
      'ActionNegotiationSelectEudora',
      'ActionNegotiationRequestAgreementBill',
    ]),
    onNegociarBoticario(debt) {
      this.ActionNegotiationSelectBoticario(debt);
    },
    onNegociarEudora() {
      this.ActionNegotiationSelectEudora();
    },
    async onRequestSecondBill({ item, brand }) {
      try {
        await this.ActionNegotiationRequestAgreementBill({ brand, agreementItem: item });
      } catch (e) {
        const msg = e && e.message
          ? e.message
          : 'Não foi possível carregar a 2ª via. Tente novamente.';
        flashMessages.error(msg, 'Erro', 4500);
      }
    },
  },
};
</script>

<style scoped>
.dashboard {
  padding-top: 8px;
  padding-bottom: 32px;
}

.dashboard__debts-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  margin-top: 16px;
}

.dashboard__greeting {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 6px;
}

.dashboard__hello {
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e1e24;
  margin: 0;
  line-height: 1.3;
}

.dashboard__cpf {
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-style: normal;
  color: #666;
  margin: 0;
}

.dashboard__section-title {
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: #3a4148;
  margin: 16px 0 16px;
  text-align: left;
}

.dashboard__section-title--offers {
  margin-top: 8px;
}

.dashboard__cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.dashboard__cards-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.dashboard__agreements-divider {
  display: block;
  width: 100%;
  border: none;
  border-top: 1px solid #e6e7e9;
  margin: 28px 0 22px;
  padding: 0;
  height: 0;
}

.dashboard__agreements-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.dashboard__agreements-heading {
  width: 100%;
  text-align: left;
  margin-bottom: 16px;
}

.dashboard__agreements-title {
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e1e24;
  margin: 0 0 6px;
  line-height: 1.3;
}

.dashboard__agreements-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 400;
  color: #555;
  margin: 0;
  line-height: 1.4;
}

.dashboard__agreements-cards-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  align-items: flex-start;
  justify-content: flex-start;
}

.dashboard__agreements-section--after-debt-cards
  > .dashboard__agreements-cards-row
  > .dashboard__agreements-wrap:first-child.dashboard__agreements-wrap--after-offers {
  margin-top: 0;
}

.dashboard__agreements-wrap {
  width: 100%;
  min-width: 300px;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}

.dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:first-child {
  margin-top: 4px;
}

.dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:first-child.dashboard__agreements-wrap--after-offers {
  margin-top: 24px;
}

.dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:not(:first-child) {
  margin-top: 24px;
}

@media (min-width: 768px) {
  .dashboard__agreements-divider {
    margin: 32px 0 26px;
  }

  .dashboard__cards,
  .dashboard__cards-group {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:first-child,
  .dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:first-child.dashboard__agreements-wrap--after-offers {
    margin-top: 0;
  }

  .dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:not(:first-child) {
    margin-top: 0;
  }

  .dashboard__agreements-wrap {
    margin-left: 0;
    margin-right: 0;
    flex: 0 1 auto;
    max-width: 100%;
    width: auto;
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .dashboard__cards {
    flex-direction: column;
  }
  .dashboard__cards-group {
    flex-direction: column;
  }

  .dashboard__agreements-heading {
    margin-bottom: 8px;
  }

  .dashboard__agreements-cards-row {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .dashboard__agreements-wrap {
    min-width: 0;
    max-width: 100%;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:first-child,
  .dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:first-child.dashboard__agreements-wrap--after-offers,
  .dashboard__agreements-section > .dashboard__agreements-cards-row > .dashboard__agreements-wrap:not(:first-child) {
    margin-top: 0;
  }
}
</style>
